import React from 'react';
import { Button, Stack, Typography, Box, Alert, Tooltip } from '@mui/material';
import BankAccountCard from '../../components/BankAccountCard/BankAccountCard';
import { rootSx, titleSx } from '../styles';
import { headerSx } from './styles';
import { Add } from '@mui/icons-material';
import { AvailableTransactionInstrumentDTO, BankAccount } from '../../service/models';

/**
 * Returns null if the bank account is selectable, or a tooltip message if it should be disabled.
 * A bank account is disabled when none of the available instruments match its eligibility.
 */
const getBankAccountDisabledReason = (
    item: BankAccount,
    availableInstruments: AvailableTransactionInstrumentDTO[],
): string | null => {
    const sepaAvailable = availableInstruments.some((i) => i.instrument === 'BANK_TRANSFER_SEPA');
    const swiftAvailable = availableInstruments.some((i) => i.instrument === 'BANK_TRANSFER_SWIFT');

    const hasMatchingInstrument =
        (sepaAvailable && item.sepaEligible) || (swiftAvailable && item.swiftEligible);

    if (hasMatchingInstrument) return null;

    // Build a message explaining why the account is disabled
    if (sepaAvailable && !swiftAvailable) {
        return 'This bank account is not available for SEPA transfers';
    }
    if (swiftAvailable && !sepaAvailable) {
        return 'This bank account is not available for SWIFT transfers';
    }
    // Both instruments available but account is eligible for neither
    return 'This bank account is not available for SEPA or SWIFT transfers';
};

interface BankAccountStepProps {
    type: 'BANK' | 'CRYPTO';
    items: any[];
    selectedId?: number | string;
    onSelect: (item: any) => void;
    onAdd: () => void;
    error?: string;
    availableInstruments?: AvailableTransactionInstrumentDTO[];
}

const BankAccountStep: React.FC<BankAccountStepProps> = ({
    type,
    items,
    selectedId,
    onSelect,
    onAdd,
    error,
    availableInstruments,
}) => {
  return (
    <Stack sx={rootSx}>
      <Stack sx={headerSx}>
        <Typography sx={titleSx}>
            {type === 'BANK' ? 'Select Bank Account' : 'Select Crypto Wallet'}
        </Typography>
        <Button variant="text" size="large" startIcon={<Add />} onClick={onAdd}>
          {type === 'BANK' ? 'Add Account' : 'Add Wallet'}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

      {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              {type === 'BANK' ? 'Aucun compte bancaire.' : 'Aucun portefeuille crypto.'}
          </Typography>
      ) : (
        <Stack spacing={2} sx={{ maxHeight: '300px', overflowY: 'auto' }}>
            {items.map((item) => {
                const id = type === 'BANK' ? item.bankAccountId : item.id;
                const title = type === 'BANK' ? (item.accountLabel || item.bankName) : (item.accountLabel || item.cryptoNetwork);
                const subtitle = type === 'BANK' ? item.bankName : item.cryptoNetwork;
                const detail = type === 'BANK' ? item.iban : (item.cryptoAddress ? item.cryptoAddress.substring(0, 16) + '...' : '');

                const disabledReason = type === 'BANK' && availableInstruments
                    ? getBankAccountDisabledReason(item as BankAccount, availableInstruments)
                    : null;
                const isDisabled = !!disabledReason;

                const card = (
                    <Box
                        key={id}
                        onClick={isDisabled ? undefined : () => onSelect(item)}
                        sx={{
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            border: selectedId === id ? '2px solid #1976d2' : '2px solid transparent',
                            borderRadius: '12px',
                            ...(isDisabled && {
                                opacity: 0.45,
                                pointerEvents: 'auto',
                            }),
                        }}
                    >
                        <BankAccountCard
                            title={title}
                            iban={detail}
                            bank={subtitle}
                        />
                    </Box>
                );

                return isDisabled ? (
                    <Tooltip key={id} title={disabledReason} arrow placement="top">
                        {card}
                    </Tooltip>
                ) : card;
            })}
        </Stack>
      )}
    </Stack>
  );
};

export default BankAccountStep;